import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API_URL } from './AdminShared';

export const CalculatorsTab = () => {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/calculators/config`);
      setConfig(res.data);
    } catch (error) {
      toast.error('Failed to load calculator settings');
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/calculators/config`, config, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success('Calculator settings saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    setConfig(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!isNaN(keys[i])) keys[i] = parseInt(keys[i]);
        obj = obj[keys[i]];
      }
      const lastKey = !isNaN(keys[keys.length - 1]) ? parseInt(keys[keys.length - 1]) : keys[keys.length - 1];
      obj[lastKey] = value;
      return copy;
    });
  };

  if (loadingConfig) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!config) return <p className="text-slate-500">No config found.</p>;

  const mc = config.mortgage_calculator || {};
  const sc = config.stamp_duty_calculator || {};

  return (
    <div className="space-y-8">
      {/* Mortgage Calculator Settings */}
      <div className="luxury-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">Mortgage Calculator</h3>
            <p className="text-sm text-slate-500 mt-1">Shown on property listing pages for sale listings</p>
          </div>
          <button
            onClick={() => updateField('mortgage_calculator.enabled', !mc.enabled)}
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${mc.enabled ? 'bg-[#E07A3A]' : 'bg-slate-300'}`}
            data-testid="calc-mortgage-toggle"
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${mc.enabled ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* UK Settings */}
        {mc.UK && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">UK Settings</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Default Rate (%)</label>
                <input type="number" step="0.25" value={mc.UK.default_interest_rate} onChange={e => updateField('mortgage_calculator.UK.default_interest_rate', parseFloat(e.target.value))}
                  className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="calc-uk-rate" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Default Term (yrs)</label>
                <input type="number" value={mc.UK.default_term_years} onChange={e => updateField('mortgage_calculator.UK.default_term_years', parseInt(e.target.value))}
                  className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="calc-uk-term" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Default Deposit (%)</label>
                <input type="number" value={mc.UK.default_deposit_percent} onChange={e => updateField('mortgage_calculator.UK.default_deposit_percent', parseInt(e.target.value))}
                  className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="calc-uk-deposit" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Max Rate (%)</label>
                <input type="number" step="0.5" value={mc.UK.max_interest_rate} onChange={e => updateField('mortgage_calculator.UK.max_interest_rate', parseFloat(e.target.value))}
                  className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="calc-uk-maxrate" />
              </div>
            </div>
          </div>
        )}

        {/* India Settings */}
        {mc.IN && (
          <div>
            <h4 className="text-sm font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">India Settings</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Default Rate (%)</label>
                <input type="number" step="0.25" value={mc.IN.default_interest_rate} onChange={e => updateField('mortgage_calculator.IN.default_interest_rate', parseFloat(e.target.value))}
                  className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="calc-in-rate" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Default Term (yrs)</label>
                <input type="number" value={mc.IN.default_term_years} onChange={e => updateField('mortgage_calculator.IN.default_term_years', parseInt(e.target.value))}
                  className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="calc-in-term" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Default Deposit (%)</label>
                <input type="number" value={mc.IN.default_deposit_percent} onChange={e => updateField('mortgage_calculator.IN.default_deposit_percent', parseInt(e.target.value))}
                  className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="calc-in-deposit" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Max Rate (%)</label>
                <input type="number" step="0.5" value={mc.IN.max_interest_rate} onChange={e => updateField('mortgage_calculator.IN.max_interest_rate', parseFloat(e.target.value))}
                  className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="calc-in-maxrate" />
              </div>
            </div>
          </div>
        )}

        {/* Canada Settings */}
        {mc.CA && (
          <div>
            <h4 className="text-sm font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">Canada Settings</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Default Rate (%)</label>
                <input type="number" step="0.25" value={mc.CA.default_interest_rate} onChange={e => updateField('mortgage_calculator.CA.default_interest_rate', parseFloat(e.target.value))}
                  className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="calc-ca-rate" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Default Term (yrs)</label>
                <input type="number" value={mc.CA.default_term_years} onChange={e => updateField('mortgage_calculator.CA.default_term_years', parseInt(e.target.value))}
                  className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="calc-ca-term" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Default Deposit (%)</label>
                <input type="number" value={mc.CA.default_deposit_percent} onChange={e => updateField('mortgage_calculator.CA.default_deposit_percent', parseInt(e.target.value))}
                  className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="calc-ca-deposit" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Max Rate (%)</label>
                <input type="number" step="0.5" value={mc.CA.max_interest_rate} onChange={e => updateField('mortgage_calculator.CA.max_interest_rate', parseFloat(e.target.value))}
                  className="w-full border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none" data-testid="calc-ca-maxrate" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stamp Duty Calculator Settings */}
      <div className="luxury-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">Stamp Duty / Land Transfer Tax Calculator</h3>
            <p className="text-sm text-slate-500 mt-1">UK SDLT bands, India state-wise rates, and Canada provincial Land Transfer Tax</p>
          </div>
          <button
            onClick={() => updateField('stamp_duty_calculator.enabled', !sc.enabled)}
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${sc.enabled ? 'bg-[#E07A3A]' : 'bg-slate-300'}`}
            data-testid="calc-stampduty-toggle"
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${sc.enabled ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* UK SDLT Bands */}
        {sc.UK && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-[#1A1A1A] mb-3">UK SDLT Bands</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-200">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left p-2 text-xs text-slate-500 font-medium">From (£)</th>
                    <th className="text-left p-2 text-xs text-slate-500 font-medium">To (£)</th>
                    <th className="text-left p-2 text-xs text-slate-500 font-medium">Rate (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {sc.UK.bands?.map((band, i) => (
                    <tr key={`row-${i}`} className="border-t border-slate-100">
                      <td className="p-2">
                        <input type="number" value={band.from} onChange={e => updateField(`stamp_duty_calculator.UK.bands.${i}.from`, parseInt(e.target.value))}
                          className="w-full border border-slate-200 px-2 py-1 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid={`calc-sdlt-band-${i}-from`} />
                      </td>
                      <td className="p-2">
                        <input type="number" value={band.to} onChange={e => updateField(`stamp_duty_calculator.UK.bands.${i}.to`, parseInt(e.target.value))}
                          className="w-full border border-slate-200 px-2 py-1 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid={`calc-sdlt-band-${i}-to`} />
                        {band.to === -1 && <span className="text-xs text-slate-400">(-1 = unlimited)</span>}
                      </td>
                      <td className="p-2">
                        <input type="number" step="0.5" value={band.rate} onChange={e => updateField(`stamp_duty_calculator.UK.bands.${i}.rate`, parseFloat(e.target.value))}
                          className="w-full border border-slate-200 px-2 py-1 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid={`calc-sdlt-band-${i}-rate`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3">
              <label className="block text-xs text-slate-500 mb-1 font-medium">Additional Property Surcharge (%)</label>
              <input type="number" step="0.5" value={sc.UK.additional_property_surcharge} onChange={e => updateField('stamp_duty_calculator.UK.additional_property_surcharge', parseFloat(e.target.value))}
                className="w-32 border border-slate-200 px-3 py-2 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid="calc-sdlt-surcharge" />
            </div>
          </div>
        )}

        {/* India State Rates */}
        {sc.IN && (
          <div>
            <h4 className="text-sm font-semibold text-[#1A1A1A] mb-3">India State Rates</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-200">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left p-2 text-xs text-slate-500 font-medium">State</th>
                    <th className="text-left p-2 text-xs text-slate-500 font-medium">Male (%)</th>
                    <th className="text-left p-2 text-xs text-slate-500 font-medium">Female (%)</th>
                    <th className="text-left p-2 text-xs text-slate-500 font-medium">Registration (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {sc.IN.states?.map((state, i) => (
                    <tr key={`row-${i}`} className="border-t border-slate-100">
                      <td className="p-2 text-[#1A1A1A] font-medium text-xs">{state.name}</td>
                      <td className="p-2">
                        <input type="number" step="0.1" value={state.stamp_duty_male} onChange={e => updateField(`stamp_duty_calculator.IN.states.${i}.stamp_duty_male`, parseFloat(e.target.value))}
                          className="w-full border border-slate-200 px-2 py-1 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid={`calc-in-state-${i}-male`} />
                      </td>
                      <td className="p-2">
                        <input type="number" step="0.1" value={state.stamp_duty_female} onChange={e => updateField(`stamp_duty_calculator.IN.states.${i}.stamp_duty_female`, parseFloat(e.target.value))}
                          className="w-full border border-slate-200 px-2 py-1 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid={`calc-in-state-${i}-female`} />
                      </td>
                      <td className="p-2">
                        <input type="number" step="0.1" value={state.registration} onChange={e => updateField(`stamp_duty_calculator.IN.states.${i}.registration`, parseFloat(e.target.value))}
                          className="w-full border border-slate-200 px-2 py-1 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid={`calc-in-state-${i}-reg`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Canada Province Rates */}
        {sc.CA && (
          <div>
            <h4 className="text-sm font-semibold text-[#1A1A1A] mb-3">Canada Provincial Land Transfer Tax</h4>
            <div className="space-y-3">
              {sc.CA.provinces?.map((province, pi) => (
                <div key={pi} className="border border-slate-200 p-3">
                  <p className="text-sm font-medium text-[#1A1A1A] mb-2">{province.name}{province.bands?.length === 0 ? ' (No LTT)' : ''}</p>
                  {province.bands?.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-slate-100">
                        <thead>
                          <tr className="bg-slate-50">
                            <th className="text-left p-1.5 text-xs text-slate-500 font-medium">From (C$)</th>
                            <th className="text-left p-1.5 text-xs text-slate-500 font-medium">To (C$)</th>
                            <th className="text-left p-1.5 text-xs text-slate-500 font-medium">Rate (%)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {province.bands.map((band, bi) => (
                            <tr key={`${province.name}-band-${bi}`} className="border-t border-slate-100">
                              <td className="p-1.5">
                                <input type="number" value={band.from} onChange={e => updateField(`stamp_duty_calculator.CA.provinces.${pi}.bands.${bi}.from`, parseInt(e.target.value))}
                                  className="w-full border border-slate-200 px-2 py-1 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid={`calc-ca-prov-${pi}-band-${bi}-from`} />
                              </td>
                              <td className="p-1.5">
                                <input type="number" value={band.to} onChange={e => updateField(`stamp_duty_calculator.CA.provinces.${pi}.bands.${bi}.to`, parseInt(e.target.value))}
                                  className="w-full border border-slate-200 px-2 py-1 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid={`calc-ca-prov-${pi}-band-${bi}-to`} />
                                {band.to === -1 && <span className="text-xs text-slate-400">(-1 = unlimited)</span>}
                              </td>
                              <td className="p-1.5">
                                <input type="number" step="0.1" value={band.rate} onChange={e => updateField(`stamp_duty_calculator.CA.provinces.${pi}.bands.${bi}.rate`, parseFloat(e.target.value))}
                                  className="w-full border border-slate-200 px-2 py-1 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid={`calc-ca-prov-${pi}-band-${bi}-rate`} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="mt-2">
                    <label className="block text-xs text-slate-500 mb-1 font-medium">First-time Buyer Rebate (C$)</label>
                    <input type="number" value={province.first_time_rebate} onChange={e => updateField(`stamp_duty_calculator.CA.provinces.${pi}.first_time_rebate`, parseInt(e.target.value))}
                      className="w-32 border border-slate-200 px-2 py-1 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid={`calc-ca-prov-${pi}-rebate`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#E07A3A] hover:bg-[#C4652E] text-white px-10 py-6 text-sm tracking-widest uppercase font-semibold"
          data-testid="calc-save-btn"
        >
          {saving ? 'Saving...' : 'Save Calculator Settings'}
        </Button>
      </div>
    </div>
  );
};
